import React, { useMemo, useState, useContext } from 'react'
import { observer } from 'mobx-react'
import { Collapse, Spin, Row, Col, Typography } from 'antd'
import { ApprovalStatus } from '../../../components/Admin/Approval/Approval'
import PartnerCommonData from '../../../components/Admin/Partner/Sections/PartnerCommonData'
import PartnerPersonalData from '../../../components/Admin/Partner/Sections/PartnerPersonalData'
import PartnerBank from '../../../components/Admin/Partner/Sections/PartnerBank'
import PartnerDocuments from '../../../components/Admin/Partner/Sections/PartnerDocuments'
import {
  ACCEPTED,
  DECLINED,
  FILLED,
  FILLED_IN_SHOPPER,
  FILLED_IN_TINKOFF,
  NONE,
  NOT_FILLED
} from '../../../Constants'
import ToArchive from '../../../components/Admin/Partner/ActionButtonModals/ToArchive'
import ToBan from '../../../components/Admin/Partner/ActionButtonModals/ToBan'
import Assets from '../../../components/Admin/Partner/Assets/Assets'
import UnBan from '../../../components/Admin/Partner/ActionButtonModals/UnBan'
import StatusHistory from '../../../components/Admin/Partner/StatusHistory/StatusHistory'
import DownloadContract from '../../../components/Admin/Partner/ContractView/DownloadContract'
import ProfileHistory from '../../../components/Admin/Partner/ProfileHistory/ProfileHistory'
import { profileScreen } from '../../../translates'
import { AppConfig, individual, partnerStatusCodes } from '../../../AppConfig'
import { ConfigContext } from '../../../context/configContext'
import DocumentDoubleView from '../../../components/Admin/Partner/Sections/DocumentDoubleView'
import PartnerInn from '../../../components/Admin/Partner/Sections/PartnerInn'
import { usePartner } from '../../../hooks/usePartner'
import { useGetPartnerId } from '../../../hooks/useGetPartnerId'

const { Panel } = Collapse
const { Title, Text } = Typography

function Partner ({ partnerPhone }) {
  const partnerId = useGetPartnerId()
  const { partnerData: partner } = useContext(ConfigContext)
  const {
    handleCommit,
    handlePartnerActions,
    mutateAdminUpdatePartnerKind,
    mutateAdminUpdatePartnerSecurityStatus,
    isLoading
  } = usePartner(partnerPhone)

  const [activeKey, setActiveKey] = useState(['common'])

  const documents = useMemo(() => {
    const vacName = profileScreen.input.vaccinacionPhoto.name
    /**
     * Фото о вакцинации должно выводиться всегда, независимо от visible, (чтоб дать возможность загрузить если его нет)
     * но если нет видимых для пользователя документов, одну вакцинацию не выводим, вернём пустой массив
     */
    const filtered = (partner?.documents ?? [])
      .filter(d => (!d.includes('bank') && partner?.profile[d]?.visible) || d === vacName)
      // убираем Инн-фото из списка документов. Фото показвыается в отдельной секции innDetails
      .filter(d => d !== profileScreen.input.innPhoto.name)
    return filtered.length === 1 && filtered[0] === vacName ? [] : filtered
  }, [partner])

  const docsStatus = useMemo(() => {
    const outcomes = documents
      // Если фото о вакцинации не загружено, считаем его принятым
      .map(d =>
        d === profileScreen.input.vaccinacionPhoto.name && !partner?.profile?.[d]?.fileId
          ? ACCEPTED
          : (partner?.profile?.[d]?.status ?? NONE)
      )
    if (outcomes.includes(NONE)) return NONE
    if (partner?.profile?.statusCode === partnerStatusCodes.correction) {
      if (outcomes.includes(DECLINED)) return DECLINED
      if (outcomes.includes(FILLED)) return FILLED
    } else {
      if (outcomes.includes(FILLED)) return FILLED
      if (outcomes.includes(DECLINED)) return DECLINED
    }
    return ACCEPTED
  }, [partner, documents])

  const { profile } = partner

  const handleChangeCollapse = keys => {
    if (!keys.includes('common')) return null
    setActiveKey(keys)
  }

  const bankDetailStatus = useMemo(() => {
    if (profile?.bankDetails?.bankDetailsSource === AppConfig.tinkoffId.bankDetailsSource) {
      return FILLED_IN_TINKOFF
    }
    if (profile?.externalBankDetails) {
      const bankAccount = profile?.bankDetails?.bankAccount?.trim()
      const bankBik = profile?.bankDetails?.bankBik?.trim()
      return bankBik?.length && bankAccount?.length ? FILLED_IN_SHOPPER : NOT_FILLED
    }
    return profile?.bankDetails?.status
  }, [profile])

  const bankDetailsForEdit = useMemo(
    () => ({
      ...profile?.bankDetails,
      allowReview: profile?.externalBankDetails ? false : profile?.bankDetails?.allowReview
    }),
    [profile]
  )

  return (
    <>
      <Row justify='space-between' align='middle'>
        <Col>
          <Title type='success'>Профиль партнера</Title>
        </Col>
        <Col>
          <ToArchive allowArchive={profile?.allowArchive} />
          <ToBan allowBan={profile?.allowBan} />
          <UnBan allowUnban={profile?.allowUnban} />
        </Col>
      </Row>
      <Spin spinning={isLoading} size='large'>
        <Collapse activeKey={activeKey} onChange={handleChangeCollapse}>
          <Panel
            key='common'
            showArrow={false}
            header={<Text className='vertical-align-sub'>Основные данные</Text>}
            extra={
              <div className='d-flex'>
                {profile?.allowContractView && (
                  <div className='mr-3'>
                    <DownloadContract partnerId={partnerId || partner?.id} />
                  </div>
                )}
                {profile?.allowProfileHistoryView && (
                  <div className='mr-3'>
                    <ProfileHistory partnerId={partnerId || partner?.id} />
                  </div>
                )}
                {profile?.allowHistoryView && (
                  <div className='mr-3'>
                    <StatusHistory partnerId={partnerId || partner?.id} />
                  </div>
                )}
                {(profile?.allowTransfer ||
                  profile?.allowReturn ||
                  profile?.allowWriteOff ||
                  profile?.assets?.allowList) && <Assets partner={partner} />}
              </div>
            }
          >
            <PartnerCommonData
              profile={profile}
              loading={isLoading}
              onPartnerActions={handlePartnerActions}
              onSubmitPartnerKind={mutateAdminUpdatePartnerKind}
              onChangeSecurityStatus={mutateAdminUpdatePartnerSecurityStatus}
              partnerId={partnerId || partner?.id}
              verifierUserName={partner?.verifierUserName}
              verifierPickTime={partner?.verifierPickTime}
            />
          </Panel>
          {profile?.personalData && profile.personalData.visible && (
            <Panel
              header='Анкетные данные'
              key='personalData'
              extra={<ApprovalStatus status={profile?.personalData?.status} />}
              destroyInactivePanel
            >
              <PartnerPersonalData
                partnerUuid={partner?.uuid}
                profile={profile}
                loading={isLoading}
                loadingPartnerData={isLoading}
                onCommit={handleCommit}
                onCommitDocument={handleCommit}
              />
            </Panel>
          )}
          {profile?.innDetails?.visible && (
            <Panel
              header='ИНН'
              key='innDocument'
              extra={
                <ApprovalStatus
                  status={profile?.innDetails?.status}
                  forceStatus={profile?.innDetails?.statusText}
                />
              }
              destroyInactivePanel
            >
              <PartnerInn profile={profile} loading={isLoading} onCommit={handleCommit} />
            </Panel>
          )}
          {profile?.bankDetails?.visible && (
            <Panel
              header='Банковские реквизиты'
              key='bankDocument'
              extra={<ApprovalStatus status={bankDetailStatus} />}
              destroyInactivePanel
            >
              <PartnerBank
                bankDetails={bankDetailsForEdit}
                bankDetailsPhoto={profile?.bankDetailsPhoto}
                loading={isLoading}
                onCommit={handleCommit}
                allowEdit={profile?.externalBankDetails ? false : profile?.bankDetails?.allowEdit}
                isIndividual={profile?.personalData?.legalForm === individual}
                personalData={profile?.personalData}
              />
            </Panel>
          )}
          {documents?.length && (
            <Panel
              header={
                <>
                  Документы
                  <DocumentDoubleView
                    loading={isLoading}
                    onCommit={handleCommit}
                  />
                </>
              }
              key='documents'
              extra={<ApprovalStatus status={docsStatus} />}
              destroyInactivePanel
            >
              <PartnerDocuments
                profile={profile}
                documents={documents}
                loading={isLoading}
                onCommit={handleCommit}
                getDocumentStatus={d => profile?.[d]?.status}
              />
            </Panel>
          )}
        </Collapse>
      </Spin>
    </>
  )
}

export default observer(Partner)
